/**
 * RechargeDialog — shown when a patient's wallet has insufficient coins to
 * generate a smart report. It presents:
 *   • Current wallet balance
 *   • Smart report cost
 *   • Recharge amount required (cost − balance)
 *   • A dynamic UPI QR code encoding **only** the required amount
 *   • The platform UPI ID with a "copy" button
 *   • A live "Payment Success Status" section that auto-polls the wallet
 *   • A manual "Refresh Payment" button
 *
 * When the wallet balance rises to at least the report cost (detected via
 * `/api/wallet/me`), the dialog fires `onSuccess(newBalance)` so the parent
 * can resume report generation **without requiring a re-upload** — the parent
 * keeps the pending file + form in state and simply re-submits.
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, Check, Loader2, RefreshCw, Zap, Wallet as WalletIcon, AlertCircle, CheckCircle2 } from "lucide-react";

export function RechargeDialog({ open, onClose, onSuccess, balance: initialBalance = 0, required = 0 }) {
  const [balance, setBalance] = useState(initialBalance);
  const [settings, setSettings] = useState({});
  const [rzpConfig, setRzpConfig] = useState(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const { user } = useAuth();
  const pollTimerRef = useRef(null);

  const coinsPerRupee = settings.coins_per_rupee || 1;
  const reportCostCoins = settings.report_cost_coins ?? 15;
  const reportCostRupees = Math.ceil(reportCostCoins / coinsPerRupee);
  const balanceRupees = Math.floor(balance / coinsPerRupee);
  const shortfallCoins = Math.max(reportCostCoins - balance, 0);
  const shortfallRupees = Math.ceil(shortfallCoins / coinsPerRupee);

  const upiId = settings.upi_id || "";
  const upiName = settings.upi_display_name || "EFGH Health";
  const upiPayUrl = upiId && shortfallRupees > 0
    ? `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(upiName)}&am=${shortfallRupees}&cu=INR&tn=${encodeURIComponent("EFGH+wallet+top-up")}`
    : "";

  // Initial + on-open fetch of settings, Razorpay config, and fresh balance.
  useEffect(() => {
    if (!open) return;
    setPaid(false);
    Promise.all([
      api.get("/public/platform-settings").catch(() => ({ data: {} })),
      api.get("/payments/config").catch(() => ({ data: { razorpay_enabled: false } })),
      api.get("/wallet/me").catch(() => ({ data: { coins: initialBalance } })),
    ]).then(([s, p, w]) => {
      setSettings(s.data || {});
      setRzpConfig(p.data || {});
      setBalance(Number(w.data?.coins ?? initialBalance));
    });
  }, [open, initialBalance]);

  // Auto-poll wallet balance every 4 s while the modal is open so patients see
  // their payment land without clicking Refresh.
  useEffect(() => {
    if (!open) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const w = await api.get("/wallet/me");
        setBalance(Number(w.data?.coins ?? balance));
      } catch { /* ignore transient errors */ }
    }, 4000);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [open]);

  // When the wallet becomes sufficient, mark success + hand off to parent.
  useEffect(() => {
    if (!open || paid) return;
    if (balance >= reportCostCoins && reportCostCoins > 0) {
      setPaid(true);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      // Small delay so patients see the success animation before the report resumes.
      setTimeout(() => { onSuccess?.(balance); }, 900);
    }
  }, [balance, reportCostCoins, open, paid, onSuccess]);

  const refresh = async () => {
    setChecking(true);
    try {
      const w = await api.get("/wallet/me");
      setBalance(Number(w.data?.coins ?? balance));
    } catch (err) { toast.error(apiError(err, "Could not refresh wallet")); }
    finally { setChecking(false); }
  };

  const copyUpi = () => {
    if (!upiId) return;
    navigator.clipboard.writeText(upiId).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const payRazorpay = async () => {
    if (shortfallRupees < 1) return;
    setPaying(true);
    try {
      const { data: order } = await api.post("/payments/order", { amount_rupees: shortfallRupees });
      const result = await openRazorpayCheckout({
        key_id: order.key_id,
        order_id: order.order_id,
        amount_paise: order.amount_paise,
        description: `EFGH · Recharge for smart report (₹${shortfallRupees})`,
        prefill: {
          name: user?.full_name || "EFGH Patient",
          email: user?.email || "",
          contact: user?.phone || "9876543210",
        },
        theme_color: "#F97316",
      });
      if (!result) { setPaying(false); return; }
      await api.post("/payments/verify", {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      // Force a wallet refresh — the polling will pick it up too.
      await refresh();
    } catch (err) {
      toast.error(apiError(err, "Payment could not be completed"));
    } finally { setPaying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="recharge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WalletIcon className="w-5 h-5 text-efgh-orange" /> Recharge to continue
          </DialogTitle>
        </DialogHeader>

        {paid ? (
          <div className="py-6 text-center" data-testid="recharge-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-success/10 grid place-items-center">
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
            <div className="mt-3 font-heading font-semibold text-ink">Payment received</div>
            <div className="text-sm text-ink/60 mt-1">Wallet balance: <b>{balance} coins</b>. Resuming your smart report…</div>
            <Loader2 className="w-5 h-5 animate-spin text-ink/40 mx-auto mt-3" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Wallet breakdown */}
            <div className="rounded-2xl border border-border p-4 bg-muted/20" data-testid="recharge-breakdown">
              <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Why the pause?</div>
              <div className="mt-2 flex items-center gap-2 text-sm text-ink/80">
                <AlertCircle className="w-4 h-4 text-warning shrink-0" />
                Your wallet balance is lower than the smart-report cost.
              </div>
              <div className="mt-3 divide-y divide-border/70 text-sm">
                <BreakdownRow label="Current wallet balance" value={`₹${balanceRupees}`} testid="row-balance" />
                <BreakdownRow label="Smart report cost" value={`₹${reportCostRupees}`} testid="row-cost" />
                <BreakdownRow label="Recharge required" value={`₹${shortfallRupees}`} bold testid="row-required" />
              </div>
            </div>

            {/* Razorpay instant pay */}
            {rzpConfig?.razorpay_enabled && (
              <Button
                onClick={payRazorpay}
                disabled={paying}
                data-testid="recharge-razorpay-btn"
                className="w-full bg-efgh-orange hover:bg-efgh-orange/90 text-white h-11"
              >
                {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Pay ₹{shortfallRupees} instantly (UPI / Card)
              </Button>
            )}

            {/* Manual UPI */}
            {upiId ? (
              <div className="rounded-2xl border border-border p-4" data-testid="recharge-upi-block">
                <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold text-center">Or scan &amp; pay via any UPI app</div>
                <div className="mt-3 mx-auto w-40 h-40 grid place-items-center bg-white rounded-lg border border-border" data-testid="recharge-qr">
                  {upiPayUrl ? <QRCodeSVG value={upiPayUrl} size={144} level="M" /> : <div className="text-xs text-ink/40 text-center px-4">Fully recharged — nothing to pay.</div>}
                </div>
                <div className="mt-3 flex items-center justify-center gap-2 text-sm">
                  <span className="text-ink/60">{upiName}:</span>
                  <span className="font-mono font-semibold text-ink" data-testid="recharge-upi-id">{upiId}</span>
                  <button type="button" onClick={copyUpi} data-testid="recharge-copy-upi" className="text-ink/40 hover:text-ink" title="Copy UPI ID">
                    {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <div className="text-[10px] text-ink/50 mt-1 text-center">QR is auto-generated for ₹{shortfallRupees} only.</div>
              </div>
            ) : (
              !rzpConfig?.razorpay_enabled && (
                <div className="p-4 text-center bg-warning/10 text-warning rounded-lg text-sm">
                  The platform admin hasn&apos;t set up any payment method yet. Please contact support.
                </div>
              )
            )}

            {/* Payment status */}
            <div className="rounded-2xl border border-dashed border-border p-3 flex items-center justify-between gap-2" data-testid="recharge-status">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Payment status</div>
                <div className="mt-0.5 text-sm text-ink/70">Waiting for your payment — I&apos;m checking every few seconds.</div>
              </div>
              <Button variant="outline" size="sm" onClick={refresh} disabled={checking} data-testid="recharge-refresh-btn">
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span className="ml-1.5 hidden sm:inline">Refresh</span>
              </Button>
            </div>

            <button type="button" onClick={onClose} data-testid="recharge-cancel" className="w-full text-xs text-ink/50 hover:text-ink underline underline-offset-4">
              Cancel — I&apos;ll upload again later
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BreakdownRow({ label, value, bold = false, testid }) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testid}>
      <span className={`${bold ? "text-ink font-semibold" : "text-ink/70"}`}>{label}</span>
      <span className={`${bold ? "text-efgh-orange font-heading font-bold text-lg" : "text-ink font-medium"}`}>{value}</span>
    </div>
  );
}
